// StarfieldHero.js — the celestial crown of every memorial page.
// A quiet drifting night sky with the soul's star pulsing at its heart,
// fading into the light page below. Pure canvas — no Three.js needed here.

import React, { useEffect, useRef } from 'react';

function StarfieldHero() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let raf;
    let stars = [];

    const init = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const W = canvas.width;
      const H = canvas.height;
      stars = Array.from({ length: Math.min(320, Math.floor(W / 4)) }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: Math.random() * 1.3 + 0.2,
        tw: Math.random() * Math.PI * 2,
        sp: 0.015 + Math.random() * 0.04,
        warm: Math.random() < 0.22,
      }));
    };

    const draw = (t) => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      for (const s of stars) {
        s.x -= s.sp;
        if (s.x < 0) s.x = W;
        const a = 0.3 + 0.4 * Math.sin(t / 1000 + s.tw);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.warm
          ? `rgba(255,225,170,${a})`
          : `rgba(190,205,240,${a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    init();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', init);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', init);
    };
  }, []);

  return (
    <div
      className="relative h-[340px] md:h-[400px] overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at 50% 120%, #0a0e22 0%, #02030a 70%)' }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* The soul's star, alive above their name — positioned below the fixed
          site header (h-20 md:h-24) so it never hides beneath the nav bar */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-36 md:top-44"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#fff3d0',
          animation: 'soulStarPulse 3.8s ease-in-out infinite',
        }}
      />

      {/* Fade into the light page below */}
      <div
        className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent, rgba(250,249,247,0.9) 85%, #faf9f7)' }}
      />

      <style>{`
        @keyframes soulStarPulse {
          0%, 100% { box-shadow: 0 0 22px 8px rgba(255,220,150,0.75), 0 0 70px 30px rgba(255,205,120,0.25); }
          50% { box-shadow: 0 0 30px 12px rgba(255,225,160,0.9), 0 0 90px 40px rgba(255,205,120,0.35); }
        }
        @keyframes soulBreathe {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.08); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default StarfieldHero;
